/**
 * Form Export/Import Service
 *
 * Handles API calls for exporting and importing webforms including:
 * - Exporting single forms to JSON
 * - Bulk exporting multiple forms
 * - Importing forms from JSON
 * - Validating form data before import
 */

import apiClient from './apiClient';

class FormExportImportService {
    constructor() {
        this.baseURL = '/dynamic-forms';
    }

    /**
     * Export a single form to JSON format
     * @param {string} formId - The form ID to export
     * @param {boolean} includeMetadata - Whether to include creation/update metadata
     * @returns {Promise<Object>} API response with form data
     */
    async exportForm(formId, includeMetadata = true) {
        try {
            const response = await apiClient.get(
                `${this.baseURL}/forms/${formId}/export`,
                {
                    params: { include_metadata: includeMetadata }
                }
            );

            return {
                success: true,
                data: response.data,
                message: 'Form exported successfully'
            };
        } catch (error) {
            console.error('Error exporting form:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to export form',
                data: null
            };
        }
    }

    /**
     * Export multiple forms in bulk
     * @param {Array<string>} formIds - Array of form IDs to export
     * @param {boolean} includeMetadata - Whether to include creation/update metadata
     * @returns {Promise<Object>} API response with forms data
     */
    async exportFormsBulk(formIds, includeMetadata = true) {
        try {
            const response = await apiClient.post(
                `${this.baseURL}/forms/export/bulk`,
                {
                    form_ids: formIds,
                    include_metadata: includeMetadata
                }
            );

            return {
                success: true,
                data: response.data,
                message: `${response.data.count} forms exported successfully`
            };
        } catch (error) {
            console.error('Error bulk exporting forms:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to export forms',
                data: null
            };
        }
    }

    /**
     * Import a single form from JSON
     * @param {Object} formData - Form data to import
     * @param {boolean} validateOnly - Only validate without saving
     * @param {boolean} skipValidation - Skip validation warnings
     * @param {string} defaultDepartmentId - Default department ID for forms without one
     * @returns {Promise<Object>} API response with import result
     */
    async importForm(formData, validateOnly = false, skipValidation = false, defaultDepartmentId = null) {
        try {
            const requestBody = {
                forms: [formData],
                validate_only: validateOnly,
                skip_validation: skipValidation
            };

            // Add default_department_id if provided
            if (defaultDepartmentId) {
                requestBody.default_department_id = defaultDepartmentId;
            }

            const response = await apiClient.post(
                `${this.baseURL}/forms/import`,
                requestBody
            );

            return {
                success: response.data.success,
                data: response.data,
                message: response.data.message || 'Form imported successfully'
            };
        } catch (error) {
            console.error('Error importing form:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to import form',
                data: null
            };
        }
    }

    /**
     * Import multiple forms in bulk
     * @param {Array<Object>} formsData - Array of form data to import
     * @param {boolean} validateOnly - Only validate without saving
     * @param {boolean} skipValidation - Skip validation warnings
     * @param {string} defaultDepartmentId - Default department ID for forms without one
     * @returns {Promise<Object>} API response with import results
     */
    async importFormsBulk(formsData, validateOnly = false, skipValidation = false, defaultDepartmentId = null) {
        try {
            const requestBody = {
                forms: formsData,
                validate_only: validateOnly,
                skip_validation: skipValidation
            };

            // Add default_department_id if provided
            if (defaultDepartmentId) {
                requestBody.default_department_id = defaultDepartmentId;
            }

            const response = await apiClient.post(
                `${this.baseURL}/forms/import/bulk`,
                requestBody
            );

            return {
                success: response.data.success,
                data: response.data,
                message: response.data.message || 'Forms imported successfully'
            };
        } catch (error) {
            console.error('Error bulk importing forms:', error);
            return {
                success: false,
                error: error.response?.data?.detail || 'Failed to import forms',
                data: null
            };
        }
    }

    /**
     * Download exported form(s) as a JSON file
     * @param {Object|Array} data - Form data to download
     * @param {string} filename - Filename for the download
     */
    downloadAsJSON(data, filename = 'forms-export.json') {
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const url = URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }

    /**
     * Read and parse a JSON file
     * @param {File} file - JSON file to read
     * @returns {Promise<Object>} Parsed JSON data
     */
    async readJSONFile(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (event) => {
                try {
                    const jsonData = JSON.parse(event.target.result);
                    resolve({
                        success: true,
                        data: jsonData
                    });
                } catch (error) {
                    reject({
                        success: false,
                        error: 'Invalid JSON file format'
                    });
                }
            };

            reader.onerror = () => {
                reject({
                    success: false,
                    error: 'Failed to read file'
                });
            };

            reader.readAsText(file);
        });
    }

    /**
     * Validate a JSON file contains valid form data
     * @param {Object} jsonData - Parsed JSON data
     * @returns {Object} Validation result
     */
    validateFormJSON(jsonData) {
        const errors = [];
        const warnings = [];

        // Check if it's an array or single object
        const forms = Array.isArray(jsonData) ? jsonData : [jsonData];

        forms.forEach((form, index) => {
            const formLabel = forms.length > 1 ? `Form ${index + 1}` : 'Form';

            // Check required fields
            if (!form.title) {
                errors.push(`${formLabel}: Missing 'title' field`);
            }
            if (!form.category_id) {
                errors.push(`${formLabel}: Missing 'category_id' field`);
            }
            // department_id is now optional - can be provided during import
            if (!form.department_id && form.department_id !== 'UNASSIGNED_DEPARTMENT' &&
                (!form.allowed_departments || form.allowed_departments.length === 0)) {
                warnings.push(`${formLabel}: No department_id or allowed_departments - you'll need to provide a default department during import`);
            }
            if (!form.sections || !Array.isArray(form.sections)) {
                errors.push(`${formLabel}: Missing or invalid 'sections' field`);
            } else if (form.sections.length === 0) {
                warnings.push(`${formLabel}: No sections defined`);
            } else {
                // Check each section has fields
                form.sections.forEach((section, sIndex) => {
                    if (!section.fields || !Array.isArray(section.fields) || section.fields.length === 0) {
                        warnings.push(`${formLabel}, Section ${sIndex + 1}: No fields defined`);
                    }
                });
            }
        });

        return {
            valid: errors.length === 0,
            errors,
            warnings,
            formsCount: forms.length
        };
    }
}

// Create and export a singleton instance
const formExportImportService = new FormExportImportService();
export { formExportImportService };
export default formExportImportService;
